import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { HeartFilled } from '@withjoy/joykit/icons';
import { Flex, Box, TextV2 } from '@withjoy/joykit';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { CircleMapPin, MapPinWithLabel } from '@shared/components/JoyInteractableMap/MapPin';
import { DirectionsRenderer } from '@react-google-maps/api';
import { JoyInteractableMap, MAP_CARDS_PORTAL_ID } from '@shared/components/JoyInteractableMap';
import { useGoogleInteractableMap } from '@shared/components/JoyInteractableMap/JoyInteractableMap.utils';
import { withDirectionsResult } from './withDirectionsResult';
import { AccommodationCombinedType } from '@apps/thanksForRsvp/utils/AccommodationCard.utils';
import { AccommodationCombined } from '@apps/guest/packages/layout-engine/widgets/Custom/components/TravelMap/components/Accommodations/Accommodation.types';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';

export interface DirectionsMapProps {
  destination: { lat: number; lng: number; photo?: string; title?: string };
  originList: { id: string; index?: number; lat: number; lng: number; hotel?: AccommodationCombined }[];
  locationBeingHovered?: string;
  gestureHandling?: 'cooperative' | 'greedy' | 'none';
  hideDirection?: boolean;
  onMapClick?: () => void;
  onOriginClick?: (id: string) => void;
}

export interface PricingComponentProps {
  hotel: AccommodationCombined;
  setIsPriceAvailable: (isPriceAvailable: boolean) => void;
}

const CircleMapPinComponent = ({
  origin,
  locationBeingHovered,
  onOriginClick,
  index
}: {
  origin: { id: string; lat: number; lng: number; index?: number; hotel?: AccommodationCombined };
  locationBeingHovered?: string;
  onOriginClick?: (id: string) => void;
  index: number;
}) => {
  const [showPricingValue] = useIsFeatureEnabled('travelShowHotelPrice');
  const showRegularHotelPricing = !origin.hotel?.customUrl && origin.hotel?.isFromAccommodationCatalog && showPricingValue;
  const isHighlighted = origin.id === locationBeingHovered;

  const isShowPricing = origin.hotel?.type === AccommodationCombinedType.Hotel && showRegularHotelPricing;

  const { price: accommodationPrice } = useTravelPlacesPricingAndBookingDetailsFragment(origin.hotel?.id, 'accommodationPlace', !isShowPricing);

  const isGreenBackground =
    (origin.hotel?.type === AccommodationCombinedType.Hotel || (origin.hotel?.type === AccommodationCombinedType.Roomblock && origin.hotel?.isJoyManagedRoomBlock)) &&
    accommodationPrice;

  const handleMapPointClick = () => {
    onOriginClick?.(origin.id);
  };

  return (
    <MapPinWithLabel
      key={origin.id}
      position={{ lat: origin.lat, lng: origin.lng }}
      isActive={isHighlighted}
      showOnTop={isHighlighted}
      onClick={handleMapPointClick}
      onDoubleClick={handleMapPointClick}
      image={origin.hotel?.photo?.url ? `url(${origin.hotel.photo.url})` : ''}
      label={
        <Flex justifyContent="center" alignItems="center" gap={2} position="relative">
          <Flex
            backgroundColor={isGreenBackground ? 'accentGreen3' : 'mono8'}
            borderRadius={'full'}
            justifyContent="center"
            alignItems="center"
            height={pxToRem(24)}
            width={pxToRem(24)}
          >
            <TextV2 typographyVariant="label3" color={'white'} fontSize={pxToRem(11)} fontWeight={600} fontFamily="Inter UI">
              {index}
            </TextV2>
          </Flex>

          {origin.hotel?.type === AccommodationCombinedType.Roomblock && !!origin.hotel?.strikeoutPricePerNight && (
            <TextV2 typographyVariant="label3" fontSize={pxToRem(13)} fontWeight={600} fontFamily="Inter UI">
              ${origin.hotel?.strikeoutPricePerNight?.toString()}
            </TextV2>
          )}
          {accommodationPrice && (
            <TextV2 fontSize={pxToRem(13)} fontWeight={600}>
              {accommodationPrice}
            </TextV2>
          )}
        </Flex>
      }
    />
  );
};

const DirectionsMapComponent: React.FC<DirectionsMapProps & { directions: google.maps.DirectionsResult | null; loading: boolean }> = ({
  directions,
  destination,
  originList,
  locationBeingHovered,
  gestureHandling,
  hideDirection: hideDirectionProps,
  onMapClick,
  onOriginClick,
  children
}) => {
  const { getCalculatedCenterAndZoom } = useGoogleInteractableMap();
  const hasDestination = destination.lat !== 0 && destination.lng !== 0;
  const hideDirection = !hasDestination || hideDirectionProps;

  const markers = originList
    .map(origin => ({ lat: origin.lat, lng: origin.lng }))
    .concat(destination)
    .filter(marker => marker.lat !== 0 || marker.lng !== 0);

  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));

  if (!getCalculatedCenterAndZoom || markers.length === 0) {
    return null;
  }

  const { center, bounds } = getCalculatedCenterAndZoom(markers);

  return (
    <JoyInteractableMap
      center={center}
      {...(markers.length > 1 && { bounds: bounds })}
      zoom={13}
      autoCenterAndZoom={markers.length > 0}
      gestureHandling={gestureHandling}
      onClick={onMapClick}
    >
      {children}
      {!hideDirection && directions && (
        <DirectionsRenderer
          directions={directions}
          options={{
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: '#494DCB',
              strokeWeight: 3
            }
          }}
        />
      )}
      {hasDestination && (
        <CircleMapPin
          isDestination
          position={destination}
          showOnTop
          label={
            <Flex justifyContent="center" alignItems="center" borderRadius="50%" border="16px solid rgba(73, 77, 203, 0.20)">
              <HeartFilled size="md" color="white" title={destination.title} />
            </Flex>
          }
        />
      )}

      {isMobile && <Box id={MAP_CARDS_PORTAL_ID} position="fixed" width={`calc(100% - ${pxToRem(32)})`} padding={5} bottom={0} left={0} />}
      {originList.map((origin, index) => (
        <CircleMapPinComponent
          key={origin.id}
          origin={origin}
          index={origin.index ? origin.index + 1 : ++index}
          locationBeingHovered={locationBeingHovered}
          onOriginClick={onOriginClick}
        />
      ))}
    </JoyInteractableMap>
  );
};

const DirectionsMap = withDirectionsResult(DirectionsMapComponent);

DirectionsMap.displayName = 'DirectionsMap';

export { DirectionsMap };
